import { Image } from 'expo-image';
import React from 'react';
import { ImageSourcePropType, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button, { ButtonProps } from 'shared/components/vxl/Button/Button';
import Illustration, {
  IllustrationKeyType,
} from 'shared/components/vxl/Illustration/Illustration';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import {
  AnalyticsProps,
  useViewDidFocusScreenAnalyticsEvent,
} from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import theme from 'shared/styles/theme';

export interface CelebrationScreenProps extends Omit<AnalyticsProps, 'key'> {
  backgroundColor?: string; // TODO: JT-114405 replace string with background color utility
  backgroundTexture?: ImageSourcePropType;
  illustrationKey: IllustrationKeyType;
  illustrationAccessibilityLabel: string;
  bodyText: string;
  captionText?: string;
  button: Pick<
    ButtonProps,
    | 'label'
    | 'isDisabled'
    | 'onPress'
    | 'isLoading'
    | 'analyticsId'
    | 'onDark'
    | 'customAttributes'
  >;
  textColor?: string;
  screenAnalyticsEventKey?: string | undefined;
}

const CelebrationScreen = ({
  backgroundColor = theme.color.background.primary,
  backgroundTexture,
  bodyText,
  button,
  captionText,
  textColor = theme.color.text.primary,
  screenAnalyticsEventKey,
  customAttributes = null,
  illustrationAccessibilityLabel,
  illustrationKey,
}: CelebrationScreenProps) => {
  const analyticsProps = screenAnalyticsEventKey
    ? { key: screenAnalyticsEventKey, customAttributes }
    : null;
  useViewDidFocusScreenAnalyticsEvent(analyticsProps);
  const insets = useSafeAreaInsets();

  return (
    <View style={[styles.container, { backgroundColor: backgroundColor }]}>
      {backgroundTexture ? (
        <Image source={backgroundTexture} style={styles.backgroundTexture} />
      ) : null}
      <View
        style={[
          styles.inner,
          {
            paddingBottom: insets.bottom
              ? insets.bottom + theme.spacing.m
              : theme.spacing.xl,
            paddingTop: insets.top,
          },
        ]}
      >
        <View style={styles.illustrationContainer}>
          <Illustration
            illustration={illustrationKey}
            accessibilityLabel={illustrationAccessibilityLabel}
          />
        </View>
        <View style={styles.contentContainer}>
          <BodyBaseText style={[styles.textWrapper, { color: textColor }]}>
            {bodyText}
          </BodyBaseText>
          {captionText ? (
            <BodyXSText style={[styles.textWrapper, { color: textColor }]}>
              {captionText}
            </BodyXSText>
          ) : null}
          <Button
            {...button}
            analyticsId={button?.analyticsId}
            customAttributes={button?.customAttributes}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  backgroundTexture: {
    bottom: 0,
    flex: 1,
    height: '100%',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    width: '100%',
  },
  container: {
    flex: 1,
  },
  contentContainer: {
    gap: theme.spacing.m,
    justifyContent: 'flex-end',
  },
  illustrationContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    width: '100%',
  },
  inner: {
    flex: 1,
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.m,
  },
  textWrapper: {
    textAlign: 'center',
  },
});

export default CelebrationScreen;
